import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

interface PageTransitionProps {
  children: React.ReactNode
  className?: string
}

export const PageTransition: React.FC<PageTransitionProps> = ({ 
  children, 
  className = "" 
}) => {
  const location = useLocation()
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [displayLocation, setDisplayLocation] = useState(location)

  useEffect(() => {
    if (location.pathname !== displayLocation.pathname) {
      setIsTransitioning(true)
      
      // Start exit animation
      const timer = setTimeout(() => {
        setDisplayLocation(location)
        setIsTransitioning(false)
      }, 150) // Half of transition duration
      
      return () => clearTimeout(timer)
    }
  }, [location, displayLocation])

  return (
    <div className={`relative overflow-hidden ${className}`}>
      <div
        className={`transition-all duration-300 ease-in-out ${
          isTransitioning 
            ? 'opacity-0 translate-x-4 scale-[0.98]' 
            : 'opacity-100 translate-x-0 scale-100'
        }`}
      >
        {children}
      </div>
    </div>
  )
}

// Enhanced page transition with different animation types
interface AnimatedPageProps {
  children: React.ReactNode
  animationType?: 'slide' | 'fade' | 'scale' | 'slideUp'
  duration?: number
  className?: string
}

export const AnimatedPage: React.FC<AnimatedPageProps> = ({
  children,
  animationType = 'slide',
  duration = 300,
  className = ""
}) => {
  const location = useLocation()
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    setIsVisible(false)
    const timer = setTimeout(() => setIsVisible(true), 10)
    return () => clearTimeout(timer)
  }, [location.pathname])

  const getAnimationClasses = () => {
    const base = `transition-all duration-${duration} ease-out`
    
    switch (animationType) {
      case 'fade':
        return `${base} ${isVisible ? 'opacity-100' : 'opacity-0'}`
      case 'scale':
        return `${base} ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`
      case 'slideUp':
        return `${base} ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`
      case 'slide':
      default:
        return `${base} ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'}`
    }
  }

  return (
    <div className={`${getAnimationClasses()} ${className}`}>
      {children}
    </div>
  )
}

// Staggered animation for lists
interface StaggeredListProps {
  children: React.ReactNode[]
  delay?: number
  className?: string
}

export const StaggeredList: React.FC<StaggeredListProps> = ({
  children,
  delay = 100,
  className = ""
}) => {
  const [visibleItems, setVisibleItems] = useState<number[]>([])

  useEffect(() => {
    children.forEach((_, index) => {
      setTimeout(() => {
        setVisibleItems(prev => [...prev, index])
      }, index * delay)
    })

    return () => setVisibleItems([])
  }, [children, delay])

  return (
    <div className={className}>
      {children.map((child, index) => (
        <div
          key={index}
          className={`transition-all duration-300 ease-out ${
            visibleItems.includes(index)
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 translate-y-4'
          }`}
        >
          {child}
        </div>
      ))}
    </div>
  )
}

export default PageTransition